import { ref } from 'vue';
import { useEntryInitStore } from '@/services/useEntryInitStore';

/**
 * Block Billing Composable
 * 
 * This composable provides functionality for automatically calculating hours from description patterns
 * in time entries. It looks for patterns like [1.5] or [0.25] in the description text and sums them
 * to calculate total hours, applying proper rounding based on company settings.
 * 
 * Key Features:
 * - Automatic hour calculation from [x.x] patterns in descriptions
 * - Proper floating-point rounding based on company increment settings
 * - Prevention of double-calculation for existing entries that were manually edited
 * - Integration with company enforced rounding policies
 * 
 * Usage:
 * - NewTimeEntry.vue: Always active for convenience
 * - EditEntry.vue: Active but can be locked when user manually changes hours
 * 
 * @returns {Object} Block billing functions and utilities
 */
export function useBlockBilling() {
  const entryInitStore = useEntryInitStore();

  /**
   * Store original values for comparison to detect manual changes
   * Used to prevent overriding manually entered hours in existing entries
   */
  const originalValues = ref({
    description: '',
    hours: 0
  });

  /**
   * Sets the original values for an entry when component mounts
   * This is used to compare against later to detect manual changes
   * 
   * @param {string} description - The original description text
   * @param {number} hours - The original hours value
   */
  const setOriginalValues = (description = '', hours = 0) => {
    originalValues.value = {
      description,
      hours
    };
  };

  /**
   * Extracts hour values from description text using regex pattern
   * Looks for patterns like [1.5], [0.25], [-0.5] etc.
   * 
   * @param {string} description - The description text to search
   * @returns {Array<string>} Array of matched bracket patterns (e.g., ['[1.5]', '[0.25]'])
   */
  const getDescriptionHoursArray = description => {
    const regexp = /\[(?=.)([+-]?(\d*)(\.(\d+))?)\]/g;
    return description.match(regexp) || [];
  };

  /**
   * Sums up all the hour values from bracket patterns
   * Strips the brackets and converts to numbers before summing
   * 
   * @param {Array<string>} descriptionHoursArray - Array of bracket patterns like ['[1.5]', '[0.25]']
   * @returns {number} Sum of all hour values (e.g., 1.75)
   */
  const reduceHours = descriptionHoursArray => {
    return descriptionHoursArray.reduce((sum, e) => sum + Number(e.replace(/[()|[\]]/g, '')), 0);
  };

  /**
   * Converts hours to string with 2 decimal places (no rounding)
   * This maintains the original behavior where block billing sums are not rounded
   * 
   * @param {number} hours - The hours value to format
   * @returns {string} Hours as a string with 2 decimal places
   */
  const roundToNearestStep = hours => {
    // Original behavior: no rounding, just format to 2 decimal places
    return hours.toFixed(2);
  };

  /**
   * Determines if an existing entry had manually entered hours vs. calculated hours
   * This prevents overriding user's manual edits when they edit an existing entry
   * 
   * @param {number} originalHours - The original hours value when entry was loaded
   * @param {string} originalDescription - The original description when entry was loaded
   * @param {string|number} id - The entry ID (null for new entries)
   * @param {number} timeEntryIncrement - The company's time entry increment setting (e.g., 0.1, 0.25)
   * @returns {boolean} True if the entry appears to have manually entered hours
   */
  const oldEntryCalculation = (originalHours, originalDescription, id, timeEntryIncrement = 0.1) => {
    const originalDescriptionHoursArray = getDescriptionHoursArray(originalDescription);
    const rawHours = reduceHours(originalDescriptionHoursArray);

    // Use the original behavior: no rounding, just format to 2 decimal places
    const calculatedHours = Number(rawHours.toFixed(2));
    return id && originalHours !== calculatedHours;
  };

  /**
   * Core function that calculates hours from description patterns
   * Returns false if calculation should not proceed, otherwise returns raw hours
   * 
   * @param {string} description - The current description text
   * @param {string|number|null} id - Entry ID (null for new entries, truthy for existing)
   * @param {boolean} lockCalculating - If true, prevents calculation (user manually changed hours)
   * @param {number} timeEntryIncrement - Company time entry increment for old entry detection
   * @returns {number|false} Raw calculated hours or false if calculation should not proceed
   */
  const calculateDescriptionHours = (description, id = null, lockCalculating = false, timeEntryIncrement = 0.1) => {
    // if no description or we change hours manually
    if (!description || lockCalculating) return false;

    // if there is no [] with number in description
    const descriptionHoursArray = getDescriptionHoursArray(description);
    if (!descriptionHoursArray.length) return false;

    // if old entry has hours manually entered
    if (id) {
      if (oldEntryCalculation(originalValues.value.hours, originalValues.value.description, id, timeEntryIncrement)) {
        return false;
      }
    }

    // Don't round here - let the caller handle rounding to avoid double-rounding
    return reduceHours(descriptionHoursArray);
  };

  /**
   * Basic description change handler without increment-specific rounding
   * Used for simpler scenarios where default rounding is sufficient
   * 
   * @param {string} description - The description text to process
   * @param {string|number|null} id - Entry ID (null for new entries)
   * @param {boolean} lockCalculating - If true, prevents calculation
   * @param {Function|null} callback - Function to call with calculated hours
   * @returns {number|false} Calculated hours or false if no calculation
   */
  const handleDescriptionChange = (description, id = null, lockCalculating = false, callback = null) => {
    const calculatedHours = calculateDescriptionHours(description, id, lockCalculating);
    if (calculatedHours !== false && calculatedHours > 0) {
      // Format to 2 decimal places without rounding (original behavior)
      const finalHours = Number(calculatedHours.toFixed(2));

      // Update step based on calculated hours
      entryInitStore.updateStep(finalHours);

      // Call the callback with calculated hours if provided
      if (callback && typeof callback === 'function') {
        callback(finalHours);
      }
      return finalHours;
    }
    return false;
  };

  /**
   * Advanced description change handler that maintains original block billing behavior
   * This function preserves the original behavior where block billing sums are NOT rounded
   * 
   * @param {string} description - The description text to process
   * @param {string|number|null} id - Entry ID (null for new entries, truthy for existing)
   * @param {boolean} lockCalculating - If true, prevents calculation (set when user manually changes hours)
   * @param {Function|null} callback - Function to call with final calculated hours
   * @param {boolean} enforceRounding - Whether company has enforced rounding enabled (for validation)
   * @param {number} timeEntryIncrement - Company's time entry increment (e.g., 0.1, 0.25, 0.5)
   * @returns {number|false} Final hours (no rounding applied) or false if no calculation
   * 
   * @example
   * // For description "Meeting [1.25] and call [0.33]":
   * // Raw calculation: 1.25 + 0.33 = 1.58
   * // Result: 1.58 (no rounding applied, maintains original behavior)
   * 
   * // For description "blokv [0.25] [1.4]":
   * // Raw calculation: 0.25 + 1.4 = 1.65  
   * // Result: 1.65 (no rounding applied, maintains original behavior)
   */
  const handleDescriptionChangeWithIncrement = (description, id = null, lockCalculating = false, callback = null, enforceRounding = false, timeEntryIncrement = 0.1) => {
    const calculatedHours = calculateDescriptionHours(description, id, lockCalculating, timeEntryIncrement);
    if (calculatedHours !== false && calculatedHours > 0) {
      // Maintain original behavior: no rounding, just format to 2 decimal places
      const finalHours = Number(calculatedHours.toFixed(2));

      // Update step based on calculated hours
      entryInitStore.updateStep(finalHours);

      // Call the callback with final hours if provided
      if (callback && typeof callback === 'function') {
        callback(finalHours);
      }
      return finalHours;
    }
    return false;
  };
  return {
    originalValues,
    setOriginalValues,
    getDescriptionHoursArray,
    reduceHours,
    roundToNearestStep,
    oldEntryCalculation,
    calculateDescriptionHours,
    handleDescriptionChange,
    handleDescriptionChangeWithIncrement
  };
}